/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Account Info Delivery Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Dmitry Pavlov
 * @version 1.22.3
 * @since 1.11.0
 */

import { useEffect, useState, createElement } from 'react';

import { Formik, FormikHelpers } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@/main/modules/logging';
import Modal from '@/main/modules/modal/components/Modal';
import ModalSuccess from '@/main/modules/modal/components/ModalSuccess';
import { makeSelectGuardComponents } from '@/main/modules/role';
import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { LinkBlock } from '@components/common';
import {
    FormBlockStyled,
    FormControlStyled,
    FormHintSmallStyled,
} from '@components/common/FormStyled/FormStyled';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import {
    makeSelectPageAccountInfoTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import ModalErrorCommon from '@modules/modal/components/ModalError';
import { modalOpen } from '@modules/modal/modalActions';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import { makeSelectFeatureEnabled, makeSelectUserIsCoordinator } from '@pages/PageAuth/selectors/authSelectors';
import type { IRouteParams } from '@routes/constants/routesConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import {
    SubscriberIdPersonalDataEditValidationSchema,
} from '../../pageAccountInfoDeliveryConstant';
import { useGetAccountInfoDeliveryMetaQuery, useUpdateAccountInfoDeliveryInquiryMutation, useUpdateAccountInfoDeliveryMutation } from '../../store';
import { ClientId } from '../../types';
import type { IAccountInfoDeliveryData } from '../../types/pageAccountInfoTypes';
import { mapFormDataToUpdateRequest } from '../../utils/transformAccountDeliveryMeta';
import { AccountDeliveryInfoTitle, AccountInfoDeliveryStyled, InfoTextStyled } from '../styled/AccountInfoDeliveryStyled';
import { AccountDeliveryEdm } from './AccountDeliveryEdm';
import { AccountDeliveryEmail } from './AccountDeliveryEmail';
import { AccountDeliveryPost } from './AccountDeliveryPost';
import { EdmInfoBLock } from './EdmInfoBlock';

const AccountInfoDelivery = () => {
    const dispatch = useDispatch();

    const { clientId } = useParams<IRouteParams>();

    const [validationSchema, setValidationSchema] = useState<Yup.AnySchema>();


    const isBillingDeliveryEmailInquiryEnabled =
        useSelector(makeSelectFeatureEnabled(FeatureNameEnum.BILLING_DELIVERY_EMAIL_INQUIRY));
    const isCoordinator = useSelector(makeSelectUserIsCoordinator());
    const componentsFeature = useSelector(makeSelectGuardComponents());
    const isCustomRoleCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_DOCS_MANAGE);

    const isCheckboxEnabled = isCoordinator || isCustomRoleCheckAvailable;

    const { delivery: tDelivery, success: tSuccess } = useSelector(makeSelectPageAccountInfoTranslation());
    const tCommon = useSelector(makeSelectTranslationCommon());

    const {
        data: deliveryMeta,
        isLoading: deliveryMetaIsLoading,
        isFetching: deliveryMetaIsFetching,
        isSuccess: deliveryMetaIsSuccess,
    } = useGetAccountInfoDeliveryMetaQuery(ClientId(clientId));

    const [updateDeliveryMeta, {
        isLoading: isUpdating,
        isError: isUpdateError,
        error: updatingError,
    }] = useUpdateAccountInfoDeliveryMutation();

    const [updateDeliveryMetaInquiry, {
        isLoading: isUpdatingInquiry,
        isError: isUpdateErrorInquiry,
        isSuccess: isUpdateInquirySuccess,
        error: updatingErrorInquiry,
        data: updateInquiryData,
    }] = useUpdateAccountInfoDeliveryInquiryMutation();

    const initialValues: IAccountInfoDeliveryData = !!deliveryMeta ? {
        ...deliveryMeta.deliveryData,
    } : {} as IAccountInfoDeliveryData;

    useEffect(() => {
        if (isUpdateError || isUpdateErrorInquiry) {
            const errorCode = isBillingDeliveryEmailInquiryEnabled ? updatingErrorInquiry.code : updatingError.code;
            const errorMsg = tCommon.error.codes[errorCode ?? ''];

            dispatch(modalOpen({
                render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    translation: tCommon.modal,
                    children: errorMsg ?? tCommon.error.defaultMessage,
                }),
            }));
        }

        if (isUpdateInquirySuccess && isBillingDeliveryEmailInquiryEnabled && !!updateInquiryData?.inquiryId) {
            const successMessage = stringFormatParams(
                tSuccess.successMessage,
                { 'inquiryId': String(updateInquiryData.inquiryId) },
            );
            dispatch(modalOpen({
                render: (props: IModalRenderProps) => createElement(ModalSuccess, {
                    onCloseModal: props.onCloseModal,
                    translation: tCommon.modal,
                    title: '',
                    children: <Modal.Text>{successMessage}</Modal.Text>,
                }),
            }));
        }
    }, [
        componentsFeature,
        dispatch,
        isBillingDeliveryEmailInquiryEnabled,
        isUpdateError,
        isUpdateErrorInquiry,
        isUpdateInquirySuccess,
        tCommon.error.codes,
        tCommon.error.defaultMessage,
        tCommon.modal,
        tSuccess.successMessage,
        updateInquiryData?.inquiryId,
        updatingError,
        updatingErrorInquiry,
    ]);

    useEffect(() => {
        setValidationSchema(SubscriberIdPersonalDataEditValidationSchema(tCommon.form.validation));
    }, [tCommon.form.validation]);

    const update = (data: IAccountInfoDeliveryData) => {
        const deliveryData = mapFormDataToUpdateRequest(data);

        if (isBillingDeliveryEmailInquiryEnabled) {
            const initialEmail = initialValues.email ?? '';
            const deliveryDataEmail = deliveryData.email ?? '';
            const isEmailDeliveryChanged = initialValues.useEmail !== deliveryData.useEmail;
            const isEmailChanged = initialEmail !== deliveryDataEmail;
            const deliveryDataInquiry = { ...deliveryData, isEmailDeliveryChanged, isEmailChanged };
            updateDeliveryMetaInquiry({ clientId: ClientId(clientId), deliveryData: deliveryDataInquiry });
            dispatch(logBusinessEvent(
                {
                    eventName: ELogBusinessEventName.USER_DELIVERY_EMAIL_CHANGE,
                    type: ELogBusinessEventType.common,
                },
            ));
        } else {
            updateDeliveryMeta({ clientId: ClientId(clientId), deliveryData });
        }
    };

    const handleSubmit = (values: IAccountInfoDeliveryData, actions: FormikHelpers<IAccountInfoDeliveryData>) => {
        if (!!values) {
            const requestValues: IAccountInfoDeliveryData = { ...values };

            actions.resetForm({ values });
            update(requestValues);
        }
    };

    return <Loader showLoader={deliveryMetaIsLoading}>
        <AccountDeliveryInfoTitle>{tDelivery.title}</AccountDeliveryInfoTitle>
        {isBillingDeliveryEmailInquiryEnabled && (<InfoTextStyled>{tDelivery.infoText}</InfoTextStyled>)}
        {
            deliveryMetaIsSuccess && <AccountInfoDeliveryStyled>
                <Loader showLoader={isUpdating || isUpdatingInquiry || deliveryMetaIsFetching}>
                    <Formik
                        enableReinitialize
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {
                            ({
                                dirty,
                                isSubmitting,
                                isValid,
                                resetForm,
                                submitForm,
                                values,
                            }) => {
                                const isEdmVisible = !!deliveryMeta.edm?.isVisible;
                                return (
                                    <>
                                        <AccountDeliveryEmail />
                                        {isEdmVisible && (
                                            <>
                                                {isCheckboxEnabled && (
                                                    <EdmInfoBLock
                                                        edmDescriptionLink={deliveryMeta.edm?.descriptionLink}
                                                    />
                                                )}
                                                <AccountDeliveryEdm deliveryMeta={deliveryMeta} />
                                            </>
                                        )}
                                        <AccountDeliveryPost
                                            address={deliveryMeta.deliveryData.address}
                                            inquiryTopic={deliveryMeta.edm?.inquiryCode}
                                            useCourier={deliveryMeta.deliveryData.useCourier}
                                            values={values}
                                        />
                                        <FormBlockStyled>
                                            <FormHintSmallStyled>
                                                <LabelMarkStyled
                                                    className='input__label-mark'>*</LabelMarkStyled>
                                                — {tDelivery.mandatoryFieldsHint}
                                            </FormHintSmallStyled>
                                        </FormBlockStyled>
                                        <FormControlStyled>
                                            <ButtonServiceUserDenied
                                                dataName='delivery-button-save'
                                                disabled={!isValid || isSubmitting || !dirty}
                                                kind='medium'
                                                type='submit'
                                                onClick={submitForm}
                                            >
                                                {tDelivery.buttonSave}
                                            </ButtonServiceUserDenied>
                                            <LinkBlock
                                                dataName='cancelButton'
                                                disabled={!dirty}
                                                onClick={() => resetForm()}
                                            >
                                                {tDelivery.buttonCancel}
                                            </LinkBlock>
                                        </FormControlStyled>
                                    </>
                                )
                            }
                        }
                    </Formik >
                </Loader >
            </AccountInfoDeliveryStyled >
        }
    </Loader >;
};

export default AccountInfoDelivery;
